import {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import DevTypes from "@wesstron/utils/Api/constants/devTypes";
import {useIsTabVisible} from "../../../hooks/useIsTabVisible";
import {getThingCameraPresenceTimeout} from "../../../utils/CameraUtils";
import {
    openStream,
    closeStream,
} from "../../../IOT/device-functions/CameraFunctions";
import {isUsingFakeData} from "../../../utils/DemoUtils";
import {getDevices} from "../../../selectors/deviceSelector";

const PRESENCE_TIMEOUT = getThingCameraPresenceTimeout();

/**
 * @param {string} gatewayId
 * @param {string} deviceId
 * @param {string | null} streamUrl
 * @return {{loading: boolean, error: boolean}}
 */
const useCameraPresence = (gatewayId, deviceId, streamUrl = null) => {
    const demo = useMemo(() => isUsingFakeData(), []);
    const devices = useSelector(getDevices);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const isTabVisible = useIsTabVisible();

    const shouldSendStreamUrl = useMemo(() => {
        if (!gatewayId || !streamUrl) return false;

        const gatewayDevice = devices.find((d) => d.DevID === gatewayId);
        return gatewayDevice?.DevType === DevTypes.PIG_COUNTER;
    }, [devices, gatewayId, streamUrl]);

    useEffect(() => {
        if (demo) {
            setError(false);
            setLoading(false);
            return;
        }

        if (!gatewayId || !deviceId || !isTabVisible) return;

        const watchStream = async () => {
            console.log(
                "watchStream",
                gatewayId,
                deviceId,
                streamUrl,
                shouldSendStreamUrl
            );
            try {
                await openStream(gatewayId, {
                    DevID: deviceId,
                    ...(shouldSendStreamUrl ? {StreamUrl: streamUrl} : {}),
                });
                setError(false);
            } catch (err) {
                console.error(err.message);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        watchStream().catch((err) => console.error(err));

        const intervalId = setInterval(watchStream, PRESENCE_TIMEOUT);

        return () => {
            clearInterval(intervalId);
            closeStream(gatewayId, {DevID: deviceId}).catch((err) =>
                console.error(err)
            );
        };
    }, [
        gatewayId,
        deviceId,
        isTabVisible,
        demo,
        streamUrl,
        shouldSendStreamUrl,
    ]);

    return useMemo(() => ({loading, error}), [loading, error]);
};

export default useCameraPresence;
